import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { UPDATE_TEMPORARY_LIVING_ASSIGNMENT_FAILURE } from 'modules/authorizations/store/types/temporaryLivingTypes';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import { updateTemporaryLivingAssignments } from 'modules/authorizations/store/actions/temporaryLiving.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useState } from 'react';

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const getStatusIdByTitle = (enumerations, title) => {
  const statuses = Array.isArray(enumerations?.temporaryLivingStatuses) ? enumerations.temporaryLivingStatuses : [];
  return statuses.find(s => s.title === title)?.id ?? ZERO_GUID;
};

const getDerivedTempLivingStatusId = (assignment, enumerations) => {
  if (assignment?.canceledDate) return getStatusIdByTitle(enumerations, 'Canceled');
  if (assignment?.onHoldDate) return getStatusIdByTitle(enumerations, 'On Hold');
  if (assignment?.movedOutDate) return getStatusIdByTitle(enumerations, 'Moved Out');
  if (assignment?.noticeGiven) return getStatusIdByTitle(enumerations, 'Notice to Vacate Sent');
  if (assignment?.movedInDate) return getStatusIdByTitle(enumerations, 'Moved In');
  if (assignment?.confirmedDate) return getStatusIdByTitle(enumerations, 'Confirmed');
  if (assignment?.optionsSentDate) return getStatusIdByTitle(enumerations, 'Options Sent');
  if (assignment?.requestedDate) return getStatusIdByTitle(enumerations, 'Requested');
  return getStatusIdByTitle(enumerations, 'Initiated');
};

const parseNoticeDays = noticeToVacate => {
  if (!noticeToVacate) return 0;

  const candidates = [
    noticeToVacate.days,
    noticeToVacate.numberOfDays,
    noticeToVacate.noticeDays,
    noticeToVacate.value,
    noticeToVacate.title
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === '') {
      continue;
    }

    const match = String(candidate).match(/\d+/);
    if (match) {
      return parseInt(match[0], 10);
    }
  }

  return 0;
};

const calculateNoticeDueDate = (estimatedMoveOut, noticeToVacates, noticeToVacateId) => {
  if (!estimatedMoveOut || !noticeToVacateId || noticeToVacateId === ZERO_GUID) {
    return null;
  }

  const selectedNotice = Array.isArray(noticeToVacates) ? noticeToVacates.find(option => option.id === noticeToVacateId) : null;
  const noticeDays = parseNoticeDays(selectedNotice);

  const moveOutDate = new Date(estimatedMoveOut);
  if (isNaN(moveOutDate.getTime())) {
    return null;
  }

  moveOutDate.setDate(moveOutDate.getDate() - noticeDays);
  return moveOutDate.toISOString();
};

const MoveInOutCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { tempLivingAssignment, enumerations, authId, disableEdit } = props;
  const tempLivingInfo = tempLivingAssignment.temporaryLivingAssignment;

  const tabs = [{ header: 'Moved In/Moved Out', order: 1, iconPath: ['fas', 'truck-moving'] }];

  const noticeToVacates =
    enumerations && enumerations.noticeToVacates && enumerations.noticeToVacates.length > 0
      ? enumerations.noticeToVacates.filter(s => s.isActive === true)
      : [{ id: ZERO_GUID, title: '' }];

  const calculatedNoticeDueDate = calculateNoticeDueDate(
    tempLivingInfo?.estimatedMoveOut,
    noticeToVacates,
    tempLivingInfo?.noticeToVacateId
  );

  const summaries = [
    {
      header: 'Estimated Move In',
      accessorKey: 'estimatedMoveInDisplay',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.estimatedMoveIn : '',
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Estimated Move Out',
      accessorKey: 'estimatedMoveOutDisplay',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.estimatedMoveOut : '',
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Actual Move In',
      accessorKey: 'movedInDate',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.movedInDate : '',
      tab: 1
    },
    {
      header: 'Notice to Vacate',
      accessorKey: 'noticeToVacateId',
      type: 'dropdown-v2',
      options: noticeToVacates,
      required: false,
      value: tempLivingInfo ? tempLivingInfo.noticeToVacateId : '',
      tab: 1
    },
    {
      header: 'Notice Due Date',
      accessorKey: 'noticeDueDate',
      type: 'date',
      required: false,
      value: calculatedNoticeDueDate,
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Notice Given',
      accessorKey: 'noticeGiven',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.noticeGiven : '',
      tab: 1
    },
    {
      header: 'Actual Move Out',
      accessorKey: 'movedOutDate',
      type: 'date',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.movedOutDate : '',
      tab: 1
    },
    {
      header: 'Exception Days',
      accessorKey: 'exceptionDays',
      type: 'number',
      required: false,
      value: tempLivingInfo ? tempLivingInfo.exceptionDays ?? 0 : 0,
      tab: 1
    }
  ];

  const safeDate = date => {
    if (!date || date === '') return null;
    return date;
  };

  const updateFields = async (tab, values) => {
    setIsLoading(true);

    let newValues = { ...tempLivingAssignment };
    const noticeDueDate = calculateNoticeDueDate(tempLivingInfo?.estimatedMoveOut, noticeToVacates, values.noticeToVacateId);

    newValues.temporaryLivingAssignment.movedInDate = safeDate(values.movedInDate);
    newValues.temporaryLivingAssignment.noticeToVacateId = safeDate(values.noticeToVacateId);
    newValues.temporaryLivingAssignment.noticeDueDate = safeDate(noticeDueDate);
    newValues.temporaryLivingAssignment.noticeGiven = safeDate(values.noticeGiven);
    newValues.temporaryLivingAssignment.movedOutDate = safeDate(values.movedOutDate);
    newValues.temporaryLivingAssignment.exceptionDays = safeDate(values.exceptionDays);
    newValues.temporaryLivingAssignment.statusId = getDerivedTempLivingStatusId(newValues.temporaryLivingAssignment, enumerations);

    if (tempLivingAssignment && tempLivingAssignment.temporaryLivingAssignmentId) {
      const resp = await props.updateTemporaryLivingAssignments(authId, tempLivingAssignment.temporaryLivingAssignmentId, newValues);
      if (resp.type === UPDATE_TEMPORARY_LIVING_ASSIGNMENT_FAILURE) {
        props.showToast('Failed to update the assignment info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
      } else {
        setIsLoading(false);
        return true;
      }
    } else {
      setIsLoading(false);
      return false;
    }
  };

  return (
    <EditableCard
      tabs={tabs}
      summaries={summaries}
      numColumns={4}
      updateFields={updateFields}
      isLoading={isLoading}
      hideEdit={disableEdit}
    />
  );
};

export default compose(
  connect(null, {
    updateTemporaryLivingAssignments,
    showToast
  })
)(MoveInOutCard);
